import { useMemo, useCallback, useState } from 'react';
import { useEntrance } from '../../context/entrance-context';
import { useTracker } from '@/contexts/tracker-context';
import { ALL_ZONES } from '../../data/entrance-types';
import { buildPathGraph } from '../graph/pathing-graph';
import { findPathsBothAges } from '../graph/pathfinder';
import { getLogicData, DEFAULT_SETTINGS } from '../logic/logic-data';
import { buildItemInventory, SONG_MAP } from '../logic/item-map';
import type { PathResult } from '../types';

export function usePathing() {
  const { state: entranceState } = useEntrance();
  const { state: trackerState } = useTracker();

  // Song state (managed locally since songs aren't in TrackerContext yet)
  const [songs, setSongs] = useState<Record<string, boolean>>({});

  const toggleSong = useCallback((ootrName: string) => {
    setSongs(prev => ({ ...prev, [ootrName]: !prev[ootrName] }));
  }, []);

  // Build item inventory from tracker + songs
  const items = useMemo(
    () => buildItemInventory(trackerState.items, songs),
    [trackerState.items, songs],
  );

  // Build the path graph from tracked entrances
  // allZones excludes Warps/Spawns/Owls for warp edge fan-out,
  // but Spawns edges are added separately in buildPathGraph
  const graph = useMemo(
    () => {
      const zones = ALL_ZONES.filter(
        z => z !== 'Warps' && z !== 'Spawns' && z !== 'Owls',
      );
      return buildPathGraph(entranceState.connections, zones as unknown as string[]);
    },
    [entranceState.connections],
  );

  // Get logic data
  const logicData = useMemo(() => getLogicData(), []);

  // Pathfinding function
  const findPath = useCallback(
    (from: string, to: string, maxPaths = 5): PathResult[] => {
      return findPathsBothAges(
        graph,
        from,
        to,
        items,
        DEFAULT_SETTINGS,
        logicData.helpers,
        maxPaths,
      );
    },
    [graph, items, logicData.helpers],
  );

  return {
    findPath,
    graph,
    songs,
    toggleSong,
    songMap: SONG_MAP,
  };
}
